import { useAtomValue, useSetAtom } from 'jotai'
import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { HStack } from '~/components/elements/HStack'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import { Jump } from '~/components/elements/Jump'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { type CustomerStrategySegmentRoute } from '~/gql/graphql'
import { useKeyedAtomEntry } from '~/hooks/jotai'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  type Brand,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import { exists, formatFilterTextGroups } from '~/utils'

import {
  brandIdsAtom,
  commentByViewKeyAtom,
  isTabulationReadyAtom,
  savedCommentByViewKeyAtom,
  segmentDisplayTypeAtom,
  selectedRouteAtom,
  tabulatedBrandIdsAtom,
} from '../atoms'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useRestoringParams } from '../hooks/useRestoringParams'
import { useSelectedBrands } from '../hooks/useSelectedBrands'
import { useSurvey } from '../hooks/useSurvey'
import { buildCommentKey } from '../utils'

import {
  fiveSegmentArrowRoutes,
  FiveSegmentPanel,
  type FiveSegmentRoute,
} from './FiveSegmentPanel'
import { nineSegmentArrowRoutes, NineSegmentPanel } from './NineSegmentPanel'
import { NpiDriverTable } from './NpiDriverTable'
import styles from './Outputs.css'

const defaultFiveSegsRoute: FiveSegmentRoute = {
  fromSegmentNumber: 4,
  toSegmentNumber: 2,
}

const defaultNineSegsRoute: CustomerStrategySegmentRoute = {
  fromSegmentNumber: 8,
  toSegmentNumber: 3,
}

const findMatchingRoute = <
  T extends { fromSegmentNumber: number; toSegmentNumber: number },
>(
  routes: ReadonlyArray<T>,
  target: { fromSegmentNumber: number; toSegmentNumber: number },
): T | undefined =>
  routes.find(
    (route) =>
      route.fromSegmentNumber === target.fromSegmentNumber &&
      route.toSegmentNumber === target.toSegmentNumber,
  )

export const Outputs = () => {
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const selectedBrands = useSelectedBrands()

  if (!isTabulationReady) {
    return null
  }

  return (
    <VStack spacing={8}>
      {selectedBrands.map((brand) => (
        // useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
        // Outputが自分の戻り値でProviderをレンダーするのではなく、呼び出し元でラップする
        <AmplitudeProvider key={brand.id} eventNameSuffix="_who_and_what">
          <Output brand={brand} />
        </AmplitudeProvider>
      ))}
    </VStack>
  )
}

type OutputProps = {
  brand: Brand
}

const Output = ({ brand }: OutputProps) => {
  const { t } = useTranslation()
  const setBrandIds = useSetAtom(brandIdsAtom)
  const setTabulatedBrandIds = useSetAtom(tabulatedBrandIdsAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)
  const savedRoute = useAtomValue(selectedRouteAtom)
  const [fiveSegsRoute, setFiveSegsRoute] =
    React.useState<FiveSegmentRoute | null>(() => {
      if (!exists(savedRoute)) {
        return defaultFiveSegsRoute
      }

      return (
        findMatchingRoute(fiveSegmentArrowRoutes, savedRoute) ??
        defaultFiveSegsRoute
      )
    })
  const [nineSegsRoute, setNineSegsRoute] =
    React.useState<CustomerStrategySegmentRoute | null>(() => {
      if (!exists(savedRoute)) {
        return defaultNineSegsRoute
      }

      return (
        findMatchingRoute(nineSegmentArrowRoutes, savedRoute) ??
        defaultNineSegsRoute
      )
    })

  const selectedRoute =
    segmentDisplayType === 'fiveSegs' ? fiveSegsRoute : nineSegsRoute

  // セグマップ(5segs/9segs)と矢印(始点終点セグメント)の組み合わせごとにコメントを
  // 独立させて持つ。組み合わせが変わると comment/savedComment は自動的に別の値
  // (未生成なら空文字)に切り替わり、以前の組み合わせの値はマップに残ったままになる。
  const commentKey = buildCommentKey({
    segmentDisplayType,
    fromSegmentNumber: selectedRoute?.fromSegmentNumber ?? 0,
    toSegmentNumber: selectedRoute?.toSegmentNumber ?? 0,
  })
  const [comment, setComment] = useKeyedAtomEntry(
    commentByViewKeyAtom,
    commentKey,
  )
  const [savedComment, setSavedComment] = useKeyedAtomEntry(
    savedCommentByViewKeyAtom,
    commentKey,
  )

  const survey = useSurvey()
  const filterTextGroups = useSurveyQuerySurveyFilterTextGroups()
  const { generate, isGenerating } = useGenerateSingleComment()
  const logEvent = useLogEvent()

  // AI送信用のテキスト化結果は NpiDriverTable（ビュー状態の持ち主）から通知される。
  // 生成ボタン押下時にだけ読めばよく、変化のたびに再レンダリングする必要はないので
  // state ではなく ref に保持する。
  const tabulationDataRef = React.useRef('')
  const handleTabulationDataChange = React.useCallback(
    (tabulationData: string) => {
      tabulationDataRef.current = tabulationData
    },
    [],
  )

  const handleAutoGenerate = React.useCallback(async () => {
    const surveyId = survey?.id
    const tabulationData = tabulationDataRef.current
    if (!exists(surveyId) || tabulationData === '') {
      return
    }

    const result = await generate({
      brandName: brand.name,
      filterDescription:
        filterTextGroups.length === 0
          ? ''
          : formatFilterTextGroups(filterTextGroups),
      filterPatternIndex: 0,
      outputType: 'whoAndWhatAnalysis',
      // このページの表示切り替えは 5segs / 9segs の2択。'none' を送ると
      // analyzer 側が汎用プロンプトにフォールバックするため使わない
      segmentMode: segmentDisplayType === 'fiveSegs' ? 'fiveSegs' : 'nineSegs',
      surveyId,
      tabulationData,
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    brand.name,
    filterTextGroups,
    generate,
    logEvent,
    segmentDisplayType,
    setComment,
    survey,
  ])

  const [panelElement, setPanelElement] = React.useState<HTMLDivElement | null>(
    null,
  )

  const panelRef = React.useCallback((element: HTMLDivElement | null) => {
    setPanelElement(element)
  }, [])

  const [tableElement, setTableElement] = React.useState<HTMLDivElement | null>(
    null,
  )

  const tableRef = React.useCallback((element: HTMLDivElement | null) => {
    setTableElement(element)
  }, [])

  const figureInfo = useFigureInfo({
    brand,
    fromSegmentNumber: selectedRoute?.fromSegmentNumber,
    toSegmentNumber: selectedRoute?.toSegmentNumber,
  })
  const arrowRestoringParams = useRestoringParams(
    selectedRoute?.fromSegmentNumber ?? 0,
    selectedRoute?.toSegmentNumber ?? 0,
    `segment-panel-${brand.id}`,
  )
  const tableRestoringParamsBase = useRestoringParams(
    selectedRoute?.fromSegmentNumber ?? 0,
    selectedRoute?.toSegmentNumber ?? 0,
    'npi-driver',
  )
  // One stable commentRef per Output lifecycle, so retyping the comment doesn't
  // churn the restoreKey on every keystroke.
  const commentRefId = React.useRef(nanoid()).current
  // Attach the commentRef only when this figure has a comment, so the saved
  // figure maps 1:1 to its DocumentImage and the comment restores via that marker.
  const tableRestoringParams =
    comment !== ''
      ? { ...tableRestoringParamsBase, commentRef: commentRefId }
      : tableRestoringParamsBase

  const handleCloseButtonClick = React.useCallback<
    EH<IconButtonProps, 'onClick'>
  >(() => {
    setBrandIds((prev) => prev.filter((id) => id !== brand.id))
    setTabulatedBrandIds((prev) => prev.filter((id) => id !== brand.id))
    logEvent('Delete brand')
  }, [brand.id, logEvent, setBrandIds, setTabulatedBrandIds])

  return (
    <VStack id={`${brand.id}`} spacing={1}>
      <HStack align="center" spacing={0}>
        <Typography variant="heading2">{brand.name}</Typography>

        <IconButton
          buttonStyle="light"
          fillMode="flat"
          icon="trash"
          onClick={handleCloseButtonClick}
        />
      </HStack>

      <HStack align="start" spacing={1}>
        {segmentDisplayType === 'fiveSegs' && (
          // useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
          // FiveSegmentPanel自身ではなく呼び出し元でラップする
          <AmplitudeProvider eventNameSuffix="_5segs">
            <div ref={panelRef} id={`segment-panel-${brand.id}`}>
              <FiveSegmentPanel
                activeRoute={fiveSegsRoute}
                onRouteChange={setFiveSegsRoute}
              />
            </div>
          </AmplitudeProvider>
        )}
        {segmentDisplayType === 'nineSegs' && (
          <AmplitudeProvider eventNameSuffix="_9segs">
            <div ref={panelRef} id={`segment-panel-${brand.id}`}>
              <NineSegmentPanel
                selectedRoute={nineSegsRoute}
                onRouteChange={setNineSegsRoute}
              />
            </div>
          </AmplitudeProvider>
        )}

        <ShareDropDownMenuButton
          downloadAsPng={
            panelElement
              ? {
                  image: panelElement,
                  name: `${segmentDisplayType === 'fiveSegs' ? '5segs' : '9segs'}-diagram`,
                }
              : undefined
          }
          getSharedUrl={
            selectedRoute
              ? { restoringParams: arrowRestoringParams }
              : undefined
          }
          saveDocumentImage={
            selectedRoute && panelElement
              ? {
                  figureInfo,
                  image: panelElement,
                  restoringParams: arrowRestoringParams,
                }
              : undefined
          }
        />
      </HStack>

      {selectedRoute && (
        <div className={styles.commentTableGroup}>
          <AnalysisCommentBlock
            isGenerating={isGenerating}
            savedComment={savedComment}
            saveDocumentImage={
              tableElement
                ? {
                    commentReadOnly: true,
                    figureInfo,
                    image: tableElement,
                    initialComment: comment,
                    onSaved: (saved) => setSavedComment(saved),
                    restoringParams: tableRestoringParams,
                  }
                : undefined
            }
            value={comment}
            onAutoGenerate={handleAutoGenerate}
            onCommentChange={setComment}
          />
          <div ref={tableRef}>
            <NpiDriverTable
              brand={brand}
              commentValue={comment}
              fromSegmentNumber={selectedRoute.fromSegmentNumber}
              saveRestoringParams={tableRestoringParams}
              toSegmentNumber={selectedRoute.toSegmentNumber}
              onCommentSaved={(saved) => setSavedComment(saved)}
              onTabulationDataChange={handleTabulationDataChange}
            />
          </div>
        </div>
      )}

      <Jump className={styles.jump} hash="top">
        <Typography variant="link3">{t('back_to_top')}</Typography>
      </Jump>
    </VStack>
  )
}
